import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { AuthFactorType, IAuthSmsOptedOut, SecondFactorMethod } from '@repo/types'

/**
 * VRG-490 — client for `AuthAdminController`'s `2fa/*` routes
 * (`apps/api/src/infrastructure/controllers/auth.admin.controller.ts`).
 * Mirrors the response DTOs 1:1 rather than importing the API's DTO
 * classes directly (the existing house pattern — see
 * `useChangePasswordMutation`/`useChangeEmailMutation`, which do the same).
 */

export interface AuthFactor {
  id: string
  type: AuthFactorType
  label: string | null
  phoneLastFour: string | null
  verified: boolean
  /** The factor a sign-in challenges first (the user's choice, else TOTP-then-SMS). */
  preferred?: boolean
  /** False when the role policy no longer allows this method (kept so it can be removed). */
  allowed?: boolean
  lastUsedAt: string | null
  /**
   * SMS only: when a text to this number was refused because it replied STOP (Twilio 21610).
   * Null/absent when texts work. Cleared by the API on the next text that goes through.
   */
  optedOutAt?: string | null
  createdAt: string
}

export interface StartTotpEnrollmentResponse {
  factorId: string
  secret: string
  otpauthUri: string
  qrCodeDataUri: string
}

export interface StartSmsEnrollmentResponse {
  reference: string
}

export interface ConfirmAuthFactorResponse {
  factor: {
    id: string
    type: AuthFactorType
    label: string | null
    verified_at: string
  }
  // Present ONLY when this confirm was the caller's first verified factor
  // overall — see IAuthFactorEnrollmentConfirmResponse's docblock.
  recoveryCodes?: string[]
}

type AuthFactorApiError = Error & { status?: number }

const toMutationError = (status: number, fallbackMessage: string, message?: string): AuthFactorApiError => {
  const error = new Error(message || fallbackMessage) as AuthFactorApiError
  error.status = status
  return error
}

/**
 * VRG-490 — a rejected DTO (e.g. an SMS-enrollment phone the global
 * `ValidationPipe` couldn't parse) responds with `message: "Validation
 * errors"` — a fixed, generic string (`ERRORS[ErrorCode.DTO_VALIDATION_
 * FAILED]`, packages/types/src/errors/error-code.enum.ts) — and puts the
 * actual per-field reason in `details` (`{[field]: string[]}`,
 * `formatValidationErrors`, apps/api/src/libs/helpers). Reading `message`
 * alone surfaced the literal words "Validation errors" to the user, which
 * names nothing they can act on.
 *
 * Same fix SignUpPage.tsx's local `extractMessage` already applies for the
 * same reason (its own comment: "message alone tells the user nothing they
 * can act on") — pulled out here rather than re-derived a third time, since
 * every mutation in this file can hit the same DTO-validation shape.
 */
const extractApiErrorMessage = (data: unknown, fallback: string): string => {
  const details = (data as { details?: Record<string, unknown> } | undefined)?.details
  const fieldMessages = Object.values(details ?? {})
    .flat()
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
  if (fieldMessages.length > 0) return fieldMessages[0]

  const message = (data as { message?: unknown } | undefined)?.message
  if (typeof message === 'string' && message !== 'Validation errors') return message
  if (Array.isArray(message) && message.length > 0 && typeof message[0] === 'string') return message[0]

  return fallback
}

const AUTH_FACTORS_QUERY_KEY = ['auth', 'factors'] as const

// Before the policy field existed the API allowed both; keep that as the fallback so an
// older API never hides a method.
const DEFAULT_ALLOWED_METHODS: SecondFactorMethod[] = ['totp', 'sms']

export const useAuthFactorsQuery = () => {
  const query = useQuery<{ factors: AuthFactor[]; allowedMethods: SecondFactorMethod[] }>({
    queryKey: AUTH_FACTORS_QUERY_KEY,
    queryFn: async () => {
      const response = await createApiCall({ method: 'GET', url: '/auth/admin/2fa/factors' })
      if (response.status !== 200) {
        throw toMutationError(response.status, 'Failed to load your two-factor methods')
      }
      return {
        factors: (response.data?.factors ?? []) as AuthFactor[],
        allowedMethods: (response.data?.allowedMethods ?? DEFAULT_ALLOWED_METHODS) as SecondFactorMethod[],
      }
    },
  })

  return {
    factors: query.data?.factors ?? [],
    /** Second-factor methods this account's role may use (`SECOND_FACTOR_POLICY` on the API). */
    allowedMethods: query.data?.allowedMethods ?? DEFAULT_ALLOWED_METHODS,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

/** Choose which enrolled factor a sign-in challenges first. */
export const useSetPreferredAuthFactorMutation = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<void, AuthFactorApiError, { factorId: string }>({
    mutationFn: async ({ factorId }) => {
      await createApiCall({ method: 'PUT', url: `/auth/admin/2fa/factors/${factorId}/preferred` })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY })
    },
  })
  return {
    setPreferredAuthFactorAsync: mutation.mutateAsync,
    isSettingPreferredAuthFactor: mutation.isPending,
  }
}

export const useStartTotpEnrollmentMutation = () => {
  const mutation = useMutation<StartTotpEnrollmentResponse, AuthFactorApiError, void>({
    mutationFn: async () => {
      const response = await createApiCall({ method: 'POST', url: '/auth/admin/2fa/totp/enroll' })
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          'Failed to start authenticator app enrollment',
          extractApiErrorMessage(response.data, 'Failed to start authenticator app enrollment'),
        )
      }
      return response.data as StartTotpEnrollmentResponse
    },
  })
  return {
    startTotpEnrollmentAsync: mutation.mutateAsync,
    isStartingTotpEnrollment: mutation.isPending,
  }
}

export const useConfirmTotpEnrollmentMutation = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<ConfirmAuthFactorResponse, AuthFactorApiError, { code: string }>({
    mutationFn: async payload => {
      const response = await createApiCall({ method: 'POST', url: '/auth/admin/2fa/totp/confirm', data: payload })
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          "That code isn't right. Try again.",
          extractApiErrorMessage(response.data, "That code isn't right. Try again."),
        )
      }
      return response.data as ConfirmAuthFactorResponse
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY })
    },
  })
  return {
    confirmTotpEnrollmentAsync: mutation.mutateAsync,
    isConfirmingTotpEnrollment: mutation.isPending,
  }
}

export const useStartSmsEnrollmentMutation = () => {
  const mutation = useMutation<StartSmsEnrollmentResponse, AuthFactorApiError, { phone: string }>({
    mutationFn: async payload => {
      const response = await createApiCall({ method: 'POST', url: '/auth/admin/2fa/sms/enroll', data: payload })
      if (response.status !== 201) {
        if (response.status === 429) {
          throw toMutationError(response.status, "You've requested too many codes. Please wait 15 minutes and try again.")
        }
        throw toMutationError(
          response.status,
          "We couldn't send a code to that number. Check it and try again.",
          extractApiErrorMessage(response.data, "We couldn't send a code to that number. Check it and try again."),
        )
      }
      return response.data as StartSmsEnrollmentResponse
    },
  })
  return {
    startSmsEnrollmentAsync: mutation.mutateAsync,
    isStartingSmsEnrollment: mutation.isPending,
  }
}

export const useConfirmSmsEnrollmentMutation = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<ConfirmAuthFactorResponse, AuthFactorApiError, { reference: string; code: string }>({
    mutationFn: async payload => {
      const response = await createApiCall({ method: 'POST', url: '/auth/admin/2fa/sms/confirm', data: payload })
      if (response.status !== 201) {
        if (response.status === 404) {
          throw toMutationError(response.status, 'That code has expired. Request a new one.')
        }
        throw toMutationError(
          response.status,
          "That code isn't right. Try again.",
          extractApiErrorMessage(response.data, "That code isn't right. Try again."),
        )
      }
      return response.data as ConfirmAuthFactorResponse
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY })
    },
  })
  return {
    confirmSmsEnrollmentAsync: mutation.mutateAsync,
    isConfirmingSmsEnrollment: mutation.isPending,
  }
}

export const useRemoveAuthFactorMutation = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<void, AuthFactorApiError, { factorId: string }>({
    mutationFn: async ({ factorId }) => {
      const response = await createApiCall({ method: 'DELETE', url: `/auth/admin/2fa/factors/${factorId}` })
      if (response.status !== 200) {
        throw toMutationError(
          response.status,
          'Failed to remove that method',
          extractApiErrorMessage(response.data, 'Failed to remove that method'),
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_FACTORS_QUERY_KEY })
    },
  })
  return {
    removeAuthFactorAsync: mutation.mutateAsync,
    isRemovingAuthFactor: mutation.isPending,
  }
}

export const useRegenerateRecoveryCodesMutation = () => {
  const mutation = useMutation<{ recoveryCodes: string[] }, AuthFactorApiError, void>({
    mutationFn: async () => {
      const response = await createApiCall({ method: 'POST', url: '/auth/admin/2fa/recovery-codes/regenerate' })
      if (response.status !== 201) {
        throw toMutationError(
          response.status,
          'Failed to generate new recovery codes',
          extractApiErrorMessage(response.data, 'Failed to generate new recovery codes'),
        )
      }
      return response.data as { recoveryCodes: string[] }
    },
  })
  return {
    regenerateRecoveryCodesAsync: mutation.mutateAsync,
    isRegeneratingRecoveryCodes: mutation.isPending,
  }
}

/**
 * Step-up before adding ANOTHER factor (security review 2026-10-09): re-prove an existing
 * factor first, so a stolen access token alone cannot enroll an attacker's phone. Start opens
 * a challenge on the caller's preferred factor (texting a code when it is SMS); verify earns
 * a 5-minute grant the enrollment calls then rely on.
 */
export interface StepUpChallenge {
  ticket: string
  factorType: AuthFactorType
  maskedPhone?: string
  /** Every factor type this step-up may be completed with; >1 offers "use another method". */
  availableFactorTypes?: AuthFactorType[]
  /** The SMS factor's number replied STOP, so no code was texted. */
  smsOptedOut?: IAuthSmsOptedOut
}

export const useStartStepUpMutation = () => {
  const mutation = useMutation<StepUpChallenge, AuthFactorApiError, void>({
    mutationFn: async () => {
      const response = await createApiCall({ method: 'POST', url: '/auth/admin/2fa/step-up/start' })
      return response.data as StepUpChallenge
    },
  })
  return { startStepUpAsync: mutation.mutateAsync, isStartingStepUp: mutation.isPending }
}

export const useVerifyStepUpMutation = () => {
  const mutation = useMutation<void, AuthFactorApiError, { ticket: string; code: string }>({
    mutationFn: async (payload) => {
      await createApiCall({ method: 'POST', url: '/auth/admin/2fa/step-up/verify', data: payload })
    },
  })
  return { verifyStepUpAsync: mutation.mutateAsync, isVerifyingStepUp: mutation.isPending }
}

/** True when the API refused an enrollment because a step-up is needed first. */
export const isStepUpRequired = (error: unknown): boolean =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'STEP_UP_REQUIRED'

/**
 * STEP_UP_REQUIRED with no factor left to step up with (one was removed earlier in this
 * session): only a fresh sign-in can authorise adding one.
 */
export const needsSignInAgain = (error: unknown): boolean =>
  (error as { response?: { data?: { details?: { signInAgain?: boolean } } } })?.response?.data?.details
    ?.signInAgain === true
